import React, { useState, useEffect, useRef } from "react";
import { ClientSuggestion } from "../types";
import { suggestClients } from "../services/treatmentPlanSessions";
import "./CreateSessionModal.css";

interface CreateSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSession: (clientName: string, dateOfBirth: string | null) => Promise<void>;
}

const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  isOpen,
  onClose,
  onCreateSession,
}) => {
  const [clientName, setClientName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [suggestions, setSuggestions] = useState<ClientSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (clientName.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const results = await suggestClients(clientName);
        setSuggestions(results);
      } catch (err) {
        console.error("Failed to fetch suggestions:", err);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [clientName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setClientName("");
      setDateOfBirth("");
      setError(null);
      setSuggestions([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSelectSuggestion = (suggestion: ClientSuggestion) => {
    setClientName(suggestion.client_name);
    setDateOfBirth(suggestion.date_of_birth || "");
    setShowSuggestions(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const handleCreateSession = async () => {
    if (!clientName.trim()) {
      setError("Please enter a client name");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await onCreateSession(clientName.trim(), dateOfBirth || null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create session");
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isCreating && clientName.trim()) {
      handleCreateSession();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-session-modal-overlay" onClick={onClose}>
      <div className="create-session-modal" onClick={(e) => e.stopPropagation()}>
        <div className="create-session-modal__header">
          <h2>New Treatment Plan</h2>
          <button
            className="create-session-modal__close-btn"
            onClick={onClose}
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <div className="create-session-modal__content">
          <div className="form-group">
            <label htmlFor="clientName">Client Name *</label>
            <div className="input-with-suggestions" ref={suggestionsRef}>
              <input
                ref={inputRef}
                type="text"
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                placeholder="Enter client name..."
                autoComplete="off"
                disabled={isCreating}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <span className="suggestion-name">{suggestion.client_name}</span>
                      {suggestion.date_of_birth && (
                        <span className="suggestion-dob">DOB: {formatDate(suggestion.date_of_birth)}</span>
                      )}
                      <span className="suggestion-count">{suggestion.session_count} session(s)</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth</label>
            <input
              type="date"
              id="dateOfBirth"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isCreating}
            />
          </div>

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="create-session-modal__footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateSession}
            disabled={isCreating || !clientName.trim()}
          >
            {isCreating ? "Creating..." : "Create Session"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSessionModal;
